import React, {useContext, useEffect, useState} from 'react';
import JobListViewContext, { RightClickContext } from '../JobListViewContext';
import { JobNames } from '../../../framework.core/JobNames';
import { ActionButtonItem, borderColors, splitMenuItemsIntoSections } from '../../../ui/ActionButton';
import ToolboxMenuItem from '../../../uiControls/ToolboxMenuItem';
import ListViewShortcutMenu from '../ListViewShortcutMenu';
import useFunctionKeys from '../../../hooks/useFunctionKeys';
import AbstractListView, { DataFetchResult, ExportRowRenderer, ListCustomFilterRenderer, ListOrderBy, ListRowRenderer } from '../AbstractListView';
import { Search } from '../../../filtering';
import constants from '../../../constants';
import { DateRenderer, DateTimeRenderer, NullableCheckboxIconRenderer, TextAlignLeftRenderer, TextRenderer } from '../../../uiControls/SmartTableRenderers';
import { reportScheduleList, TableColumn, TableSettings } from '../../../tableSettings';
import JobListSuntekCommand from '../JobListSuntekCommand';
import * as XCol from '../../../framework.core/XExportColumn';
import Menu, { IfmsMenuItem } from '../../../ui/menu/Menu';
import { ReportScheduleListItem } from '../../../fms.data/models/ReportScheduleListItem';
import { NavigationType, openNewPage } from '../../../util/navigationHelpers';
import ApplicationContext from '../../../ApplicationContext';
import { useHistory } from 'react-router';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import useGlobalYesNoDialog from '../../../hooks/useGlobalYesNoDialog';
import UniqueLinkButton from '../../../uiControls/UniqueLinkButton';
import { DeleteIconButton2, EditIconButton2, ViewIconButton2 } from '../../../ui/IconButton';
import { findSecurityTaskWithBranch } from '../../../util';
import { FmsSecTask } from '../../../fms.core/constants';
import config from '../../../config';
import { compareIso, now, nowDate } from '../../../dateUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCircle, faPauseCircle } from '@fortawesome/free-solid-svg-icons';

function useSelectedOperations() {
   const jobListContext = useContext(JobListViewContext);
   const appContext = useContext(ApplicationContext);
   const history = useHistory();

   const selectedElement = jobListContext.selectedElement as ReportScheduleListItem;

   const [showGlobalOkDialog] = useGlobalOkDialog();
   const [showGlobalYesNoDialog] = useGlobalYesNoDialog();

   async function create() {
     if (jobListContext.hasOpenedScreenStartingWithUrl(`/shipmentStatus/reportScheduling/add`)) {
       await showGlobalOkDialog(<p>New operation is already in progress</p>);
       return;
     }

     openNewPage(history, `/shipmentStatus/reportScheduling/add`, NavigationType.newPage);
   }

async function deleteEntity() {
  if (!selectedElement) {
    return;
  }

if (selectedElement.schStatus === 'Active') {
  await showGlobalOkDialog(
    <p>Cannot delete an active schedule. Please pause it first.</p>, 
    false, null, 'warning'
  );
  return;
}

  const doDelete = await showGlobalYesNoDialog(
    <div>
      <h3>Confirm to Delete?</h3>
    </div>
  );

  if (doDelete) {
    const apiResult = await appContext.api.deleteJobDetail(JobNames.ReportScheduling, {
      id: selectedElement.id,
    });
    if (apiResult && apiResult.success) {
      jobListContext.reloadData();
    } else {
      const message = apiResult?.message ?? 'Cannot delete!';
      await showGlobalOkDialog(message);
    }
  }
}

   async function edit() {
     if (!selectedElement) {
       return;
     }

     if (jobListContext.hasOpenedScreenStartingWithUrl(`/shipmentStatus/reportScheduling/edit`)) {
       await showGlobalOkDialog(<p>Edit operation is already in progress</p>);
       return;
     }

     const id = selectedElement.id;
     openNewPage(history, `/shipmentStatus/reportScheduling/edit/${id}`, NavigationType.newPage);
   }

   async function view() {
     if (!selectedElement) {
       return;
     }

     const id = selectedElement.id;

     const viewUrl = `/shipmentStatus/reportScheduling/view/${id}`;

     if (jobListContext.hasOpenedScreenStartingWithUrl(viewUrl)) {
       await showGlobalOkDialog(<p>View operation is already in progress</p>);
       return;
     }

     openNewPage(history, viewUrl, NavigationType.newPage);
  }

  return {create, deleteEntity, edit, view};
}


function RightClickMenu({dismiss}:{dismiss: VoidFunction}) {
  //const { create, deleteEntity, edit, view } = useSelectedOperations();
  const jobListContext = useContext(JobListViewContext);

  const items = [
    { label: 'Search', shortcut: 'F5', separator: false },
    { label: 'Refresh', shortcut: 'F11', separator: true },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) =>({
    label: <ToolboxMenuItem width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    index,
  }));

  function onMenuItemSelected(item:IfmsMenuItem) {
    switch (item.index) {
      case 0:
        jobListContext.triggerTableSearch('F5');
        break;
      case 1:
        jobListContext.reloadData();
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu='rightClickContext'
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: 'nowrap',
      }}
      menuStyle={(i: number) => ({
        maxHeight: '300px',
        overflowY: 'hidden',
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}

function ListCommands() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const selectedElement = jobListContext.selectedElement as ReportScheduleListItem;
  const listSec = findSecurityTaskWithBranch(appContext, FmsSecTask.AcctPrepaidAdjustment, selectedElement?.branch);

  const {edit, view, deleteEntity} = useSelectedOperations();
  
  function hasOpenedViewOrEditFor(id: number) {
    return jobListContext.hasOpenedScreenStartingWithUrl(`/shipmentStatus/reportScheduling/edit/${id}`) || jobListContext.hasOpenedScreenStartingWithUrl(`/shipmentStatus/reportScheduling/view/${id}`);
  }


  const hasSelectedRow = !!selectedElement;
  const canBeDeleted = hasSelectedRow && listSec?.grantDel;
  const canBeAdd = listSec?.grantAdd;
  const canBeEdit = hasSelectedRow && listSec?.grantEdit;

  return (
     <div className='ifms-common-no-vertical-separator-gap-margin'>
      <div className='ifms-common-vertical-gap-8'>
        <UniqueLinkButton newStyle name='reportSch-list-add-button' display="primary" link={`/shipmentStatus/reportScheduling/add`} disabled={!canBeAdd} label={''} useOnlyIcon data-tooltip='New' iconMode />
        <EditIconButton2 name='reportSch-list-edit-button' disabled={!canBeEdit} onClick={edit} useText/>
        <ViewIconButton2 name='reportSch-list-view-button' disabled={!hasSelectedRow} onClick={view} useText  />
        <DeleteIconButton2 name='reportSch-list-delete-button' disabled={!canBeDeleted} onClick={deleteEntity} useText/>
      </div>
    </div>
  );
}

function ListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function ListViewExtras() {
  useFunctionKeys({onFunction: onFunctionKey});

  const jobListContext = useContext(JobListViewContext);

  //const {create, edit, view} = useSelectedOperations();

  const [unprocessedFkey, setUnprocessedFkey] = useState<string | null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }

      doProcessFkey();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    } else if (unprocessedFkey === "F5") {
      jobListContext.triggerTableSearch('F5');
    }

    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey: string) {
    if (['F5', 'F11'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }

  return (
    <></>
  );
}

function sanitizeFileName(fileName: string) {
    return fileName
      ?.replaceAll(/\:/g, '-')                // 윈도우 파일시스템 제한 문자
      ?.replaceAll(/[/\\?%*|"<>+]/g, ' ')     // 윈도우 파일시스템 제한 문자 && 업로드 파일 URL 호출 시 특수문자가 포함되어 있을 경우 에러가 발생함("+" 문자 추가)
      ?.replaceAll(/\./g, ' ')                // 마침표를 공백으로            ???
      ?.replaceAll(/\#/g, ' ')                // #을 공백으로                ???
      ?.replaceAll(/\s+/g, ' ')               // 연속된 공백을 하나로
      ?.trim();                               // 앞뒤 공백 제거
}

function PreviewRenderer({ cell, row, data }:any) {
  const appContext = useContext(ApplicationContext);
  const l = data[row.index];
  const [showGlobalOKDialog] = useGlobalOkDialog();
  async function exportToExcel() {
    
    const userToken = appContext.user ? appContext.user.accessToken ?? '' : '';

    try {
      const response = await fetch(`${config.serverUrl}report/exportShipmentreport`, {
        method: 'POST',
        headers: {
          //'Authorization': `Bearer ${userToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: l.id, jwt: userToken })
      });

      if(response.status === 204) {
          await showGlobalOKDialog(<p>There is no Information!</p>);
          return;
      }

      const fileName = sanitizeFileName(l.reportName);

      if (response.ok) {
        //const contentType = response.headers.get('content-type');
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = (fileName+".xlsx").replaceAll(' ', '-');
        a.click();
      } else {
        console.error('error', response.status);
      }
    } catch (error) {
      console.error('error', error);
    } finally {
    }
  }

   return <div id={`${cell.column.id}-${row.index}`} className="ifms-table-default" style={{ cursor: 'pointer', color: 'blue', fontWeight: 'bold'}} onClick={exportToExcel}>View</div>
}

function StatusRenderer({cell, row, data}: any){
  const l = data[row.index];
   let color = '#159142'; let dataTooltip = 'Active'; let id = 'active';
  if(l.schStatus === 'Paused') {color = '#e69406'; dataTooltip = 'Paused'; id='paused';}
  else if (l.schStatus === 'Completed') {color= '#4e4e4e'; dataTooltip = 'Completed'; id='completed';} 
  return <div id={`schStatus_renderer`}><FontAwesomeIcon id={id}  icon={faCircle} color={color} style={{marginLeft:'5px', marginRight:'5px'}}/>{dataTooltip}</div>
}

function LastActionRenderer({cell, row, data}: any){
  return <div id={`${cell.column.id}-${row.index}`} style={{fontWeight: 'bold'}}>{cell.value}</div>
}


/**
 *
 * @class EmailLogListView
 * @extends {AbstractListView}
 */
class ReportSchedulingListView extends AbstractListView {
  async fetchAndFormatData(api: any, search: Search, pageNum: number, limit: number): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(
      JobNames.ReportScheduling,
      'ReportScheduleListItem',
      ReportScheduleListItem.calculateSearchParameters(search,ReportScheduleListItem.MapSearchFields),
      pageNum,
      limit,
      search.pageSize,
      search.pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return {data: [], totalCount: 0};
    }

    return {data: ReportScheduleListItem.formatData(apiResult.entities), totalCount: apiResult.totalCount};
  }

  async exportFormatDataWithExcel(api: any, search: Search, pageNum: number, limit: number, pageSize: number, pagePerBatch: number): Promise<Array<any>> {
    const apiResult = await api.getListItems(JobNames.ReportScheduling, 'ReportScheduleListItem',
      ReportScheduleListItem.calculateSearchParameters(search, ReportScheduleListItem.MapSearchFields),
      pageNum,
      limit,
      pageSize,
      pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return [];
    }

    return  ReportScheduleListItem.formatData(apiResult.entities);
  }

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
    ];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.oceanImportMbl;
  }

  getRowRenderers(): ListRowRenderer[] {
    return ReportScheduleListItem.getExportCollection().map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if(x.dataIndex === 'schStatus') colRenderer.Cell = StatusRenderer;
      else if(x.dataIndex === 'preview') colRenderer.Cell = PreviewRenderer;
      else if (x.dataIndex === 'lastAction') colRenderer.Cell = LastActionRenderer;
      else if(x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if(x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeRenderer;
      else if(x.type === XCol.XCellType.Bool) colRenderer.Cell = NullableCheckboxIconRenderer;
      else if(x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;
      return colRenderer;
    });
  }
  
  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({accessor: 'status', changedValue: (value: any)=> { return value['status'] === 'PC' ? 'Period Close' : ''}});
    return colRenderer;
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'id',
      orderByAscending: false,
    };
  }

  getTableSettings(): TableSettings {
    const tsettings =  reportScheduleList;
    const cols = ReportScheduleListItem.getExportCollection();
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });
    return tsettings;
  }

  renderCommands() {
    return (
      <ListCommands/>
    );
  }

  renderBeforeConfigureButton() {
    return (
      <ListToolbar/>
    );
  }

  
  hasMemo(): boolean {
    return false;
  }
  renderExtras() {
    return <ListViewExtras/>
  }

  getViewUrlFor(row: ReportScheduleListItem) {
     return row && row.id ? `/shipmentStatus/reportScheduling/view/${row.id}` : '';
  }

  getListViewTitle() {
    return 'Report Scheduling';
  };

  renderAdditionalCommands() {
    return <JobListSuntekCommand jobName={JobNames.LogMail} keyProp='id' showAdjustBalance={false} />;
  }

  hasRightClickContext(): boolean {
    return true;
  }

  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }
}

export default ReportSchedulingListView;